import { useState, useMemo, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes, faClock, faMapMarkerAlt, faEnvelope, faPhone,
  faBuilding, faTruck, faChevronDown, faChevronUp, faChevronLeft, faChevronRight,
  faMinus, faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useCarousel } from "../../hooks/useCarousel";
import { getCategoryColor } from "../../constants/materialCategories";
import { sanitizeForDisplay } from "../../utils/sanitize";
import ActionButton from "../ui/ActionButton";
import BaseDialog from "../ui/BaseDialog";
import "../../style/ListingDetailPanel.css";

const DAYS = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];

const fmtDate = (str) => {
  if (!str) return "—";
  try {
    const [y, m, d] = String(str).split(/[-T]/);
    return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return String(str).slice(0, 10); }
};

function toYMD(d) { return d.toISOString().slice(0, 10); }
function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
function toMins(t) {
  if (!t) return null;
  const m = String(t).match(/^(\d{1,2}):(\d{2})$/);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}
function normTime(t) {
  const m = toMins(t); if (m == null) return "";
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

function parseAvailDays(raw) {
  const s = new Set();
  if (raw == null || raw === "") return s;
  const n = typeof raw === "number" ? raw : /^[0-9]+$/.test(String(raw).trim()) ? Number(raw) : null;
  if (n != null) { for (let d = 0; d <= 6; d++) { if ((n & (1 << d)) !== 0) s.add(d); } return s; }
  if (typeof raw === "object" && !Array.isArray(raw)) {
    const map = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };
    Object.entries(map).forEach(([k, v]) => { if (raw?.[k]) s.add(v); }); return s;
  }
  const tokens = Array.isArray(raw) ? raw.map(String) : String(raw).split(/[,/ ]+/).filter(Boolean);
  tokens.forEach((t) => {
    const l = String(t).trim().toLowerCase();
    const map = { "0": 0, sun: 0, sunday: 0, "1": 1, mon: 1, monday: 1, "2": 2, tue: 2, tuesday: 2,
      "3": 3, wed: 3, wednesday: 3, "4": 4, thu: 4, thursday: 4, "5": 5, fri: 5, friday: 5, "6": 6, sat: 6, saturday: 6 };
    if (map[l] != null) s.add(map[l]);
  });
  return s;
}

function ListingDetailPanel({ listing, companyUser, onClose, onSubmit, darkMode }) {
  const { t } = useTranslation();
  const { images, idx, hasMultiple, prev, next, goTo } = useCarousel(listing?.images);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const toggleForm = () => setIsFormOpen((v) => !v);

  const [pickupDate, setPickupDate] = useState(null);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [minQty, setMinQty] = useState("");
  const [maxQty, setMaxQty] = useState("");
  const [maxCost, setMaxCost] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const cpu = companyUser || {};
  const title = listing?.item_name || listing?.material || "—";
  const isFree = Number(listing?.cost) === 0;
  const method = String(listing?.delivery_method || "pickup").toLowerCase();
  const isDelivery = method === "delivery";
  const isPickup = !isDelivery;
  const cost = listing?.cost ?? listing?.cost_per_lb;
  const qty = listing?.remaining_qty ?? listing?.quantity;
  const deadline = listing?.latest_pickup;
  const instructions = listing?.delivery_instructions ?? "";
  const qtyNum = parseFloat(qty) || 0;

  const availDays = useMemo(() => parseAvailDays(listing?.available_days ?? listing?.availableDays), [listing]);
  const pStart = useMemo(() => normTime(listing?.pickup_time_start ?? listing?.pickupTimeStart), [listing]);
  const pEnd = useMemo(() => normTime(listing?.pickup_time_end ?? listing?.pickupTimeEnd), [listing]);

  const minDate = useMemo(() => addDays(new Date(), 1), []);
  const maxDate = useMemo(() => {
    const y = addDays(new Date(), 365);
    if (deadline) { const dl = new Date(deadline); if (!isNaN(dl.getTime()) && dl < y) return dl; }
    return y;
  }, [deadline]);

  const disableDate = useCallback((d) => {
    if (!(d instanceof Date) || isNaN(d.getTime())) return true;
    if (!availDays || availDays.size === 0) return false;
    return !availDays.has(d.getDay());
  }, [availDays]);

  useEffect(() => {
    if (!listing) return;
    setIsFormOpen(false);
    setMinQty(""); setMaxQty(""); setMaxCost(""); setVehicle("");
    setStartTime(pStart || ""); setEndTime(pEnd || ""); setSubmitting(false);

    let d = addDays(new Date(), 1);
    for (let i = 0; i < 400 && d <= maxDate; i++) {
      if (!disableDate(d)) { setPickupDate(new Date(d)); return; }
      d = addDays(d, 1);
    }
    setPickupDate(null);
  }, [listing, maxDate, disableDate, pStart, pEnd]);

  const totalCost = useMemo(() => {
    if (!cost || !maxQty) return "$0.00";
    const q = parseFloat(maxQty);
    if (isNaN(q) || q <= 0) return "$0.00";
    const cap = qtyNum > 0 ? Math.min(q, qtyNum) : q;
    return `$${(Number(cost) * cap).toFixed(2)}`;
  }, [cost, qtyNum, maxQty]);

  // field-level validation
  const errors = useMemo(() => {
    const e = {};
    const mn = parseFloat(minQty), mx = parseFloat(maxQty);
    if (minQty && mn < 0) e.minQty = t("Cannot be negative");
    if (maxQty && mx < 0) e.maxQty = t("Cannot be negative");
    if (mn > 0 && mx > 0 && mn > mx) e.minQty = t("Min cannot exceed max");
    if (mx > 0 && qtyNum > 0 && mx > qtyNum) e.maxQty = t("Exceeds available") + ` (${qtyNum})`;
    const sM = toMins(startTime), eM = toMins(endTime);
    if (sM != null && eM != null && eM <= sM) e.endTime = t("Must be after start");
    // enforce donor's time bounds
    const loM = toMins(pStart), hiM = toMins(pEnd);
    if (sM != null && loM != null && sM < loM) e.startTime = t("Before available window");
    if (eM != null && hiM != null && eM > hiM) e.endTime = t("After available window");
    if (maxCost && parseFloat(maxCost) < 0) e.maxCost = t("Cannot be negative");
    return e;
  }, [minQty, maxQty, qtyNum, startTime, endTime, pStart, pEnd, maxCost, t]);

  const hasErrors = Object.keys(errors).length > 0;

  const handleStartChange = (val) => {
    setStartTime(val);
    const v = toMins(val), en = toMins(endTime);
    if (v != null && en != null && en < v) setEndTime(val);
  };

  const handleEndChange = (val) => {
    const v = toMins(val), s = toMins(startTime);
    if (s != null && v != null && v < s) setEndTime(startTime);
    else setEndTime(val);
  };

  const stepVal = (cur, delta, max) => {
    const n = (parseFloat(cur) || 0) + delta;
    const clamped = Math.max(0, max != null ? Math.min(n, max) : n);
    return clamped > 0 ? String(clamped) : "";
  };

  const handleSubmit = async () => {
    if (submitting || hasErrors) return;
    const lid = listing?.id ?? listing?.listing_id;
    if (!lid || !pickupDate) return;

    const payload = {
      listingId: lid,
      donorId: listing?.company_id ?? listing?.donor_id,
      minCost: parseFloat(maxCost),
      minQty: parseFloat(minQty),
      maxQty: parseFloat(maxQty),
      scheduledDate: toYMD(pickupDate),
      timeFrame: `${startTime} - ${endTime}`,
      startTime, endTime,
      pickupVehicle: isPickup ? String(vehicle).trim() : undefined,
    };
    try {
      setSubmitting(true);
      if (onSubmit) await onSubmit(payload);
      onClose?.();
    } finally { setSubmitting(false); }
  };

  if (!listing) return null;

  const cls = darkMode ? "ldp--dark" : "ldp--light";
  const canSubmit = pickupDate && startTime && endTime && minQty && maxQty && maxCost && !hasErrors;

  return (
    <div className={`ldp-wrap ${cls}`}>
      {/* panel 1: listing details */}
      <div className="ldp">
        <div className="ldp__header">
          <span className="ldp__header-title">{t("Listing Details")}</span>
          <button className="ldp__close" onClick={onClose}><FontAwesomeIcon icon={faTimes} /></button>
        </div>

        <div className="ldp__body">
          <div className="ldp__img">
            {images.length > 0 ? (
              <div className="lc__track" style={{ transform: `translateX(-${idx * 100}%)` }}>
                {images.map((src, i) => <img key={i} src={src} alt={title} onError={e => { e.target.style.display = "none"; }} />)}
              </div>
            ) : <div className="lc__img-placeholder" />}
            {hasMultiple && (
              <>
                <button className="lc__nav lc__nav--prev" onClick={prev} disabled={idx === 0} style={{ opacity: 1 }}>
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <button className="lc__nav lc__nav--next" onClick={next} disabled={idx === images.length - 1} style={{ opacity: 1 }}>
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
                <div className="lc__dots">
                  {images.map((_, i) => <span key={i} className={`lc__dot ${i === idx ? "lc__dot--active" : ""}`} onClick={(e) => goTo(e, i)} />)}
                </div>
              </>
            )}
          </div>

          <div className="ldp__badges">
            {listing.material_category && (
              <span className="ldp__badge" style={{ backgroundColor: getCategoryColor(listing.material_category) }}>{t(listing.material_category)}</span>
            )}
            <span className={`ldp__badge ${isDelivery ? "ldp__badge--delivery" : "ldp__badge--neutral"}`}>
              {isDelivery && <FontAwesomeIcon icon={faTruck} style={{ fontSize: "0.5rem", marginRight: 3 }} />}
              {t(isDelivery ? "Delivery" : "Pickup")}
            </span>
            {isFree && <span className="ldp__badge ldp__badge--free">{t("Free")}</span>}
          </div>

          <div className="ldp__company">
            <FontAwesomeIcon icon={faBuilding} className="ldp__company-icon" />
            <span className="ldp__company-name">{listing.companyName || cpu.company_name || "—"}</span>
            <span className="ldp__rating">
              <span className="ldp__rating-leaf">🌿</span>
              <span className="ldp__rating-val">4.6</span>
              <span className="ldp__rating-count">(143)</span>
            </span>
            <span className="ldp__rating-example">{t("Example")}</span>
          </div>

          {!isFree ? (
            <div className="ldp__price">
              <span className="ldp__price-num">${Number(cost || 0).toFixed(2)}</span>
              <span className="ldp__price-unit">{t("per lb")}</span>
            </div>
          ) : (
            <div className="ldp__price">
              <span className="ldp__price-num ldp__price-num--free">{t("Free")}</span>
            </div>
          )}

          <div className="ldp__deadline">
            <FontAwesomeIcon icon={faClock} className="ldp__deadline-icon" />
            <span className="ldp__deadline-label">{t(isDelivery ? "Deliver by" : "Pick up by")}</span>
            <span className="ldp__deadline-val">{fmtDate(deadline)}</span>
          </div>

          <div className="ldp__stats">
            <div className="ldp__stat">
              <span className="ldp__stat-label">{t("Available")}</span>
              <span className="ldp__stat-val">{qtyNum} lbs</span>
            </div>
            <div className="ldp__stat">
              <span className="ldp__stat-label">{t("Category")}</span>
              <span className="ldp__stat-val">{listing.material_category || "—"}</span>
            </div>
            <div className="ldp__stat">
              <span className="ldp__stat-label">{t("Form")}</span>
              <span className="ldp__stat-val">{title}</span>
            </div>
          </div>

          <div className="ldp__days-section">
            <div className="ldp__days-head">
              <span className="ldp__section-label">{t("Available Days")}</span>
              {(pStart || pEnd) && (
                <span className="ldp__time-window">
                  {pStart && pEnd ? `${pStart} – ${pEnd}` : pStart ? `from ${pStart}` : `until ${pEnd}`}
                </span>
              )}
            </div>
            <div className="ldp__days">
              {DAYS.map((label, i) => (
                <span key={i} className={`ldp__day ${availDays.has(i) || availDays.size === 0 ? "ldp__day--active" : ""}`}>{label}</span>
              ))}
            </div>
          </div>

          {cpu && (
            <div className="ldp__section">
              <span className="ldp__section-label">{t("Company Info")}</span>
              {cpu.address && (
                <div className="ldp__info-row"><FontAwesomeIcon icon={faMapMarkerAlt} className="ldp__info-icon" /><span>{cpu.address}{cpu.city ? `, ${cpu.city}` : ""}</span></div>
              )}
              {(() => {
                const sc = listing?.share_contact;
                const isSharing = sc === true || sc === 1 || sc === "1";
                const email = isSharing ? (listing?.custom_email || cpu.email) : null;
                const phone = isSharing ? (listing?.custom_phone || cpu.phone_number || cpu.phone) : null;
                return (
                  <>
                    {email && (
                      <div className="ldp__info-row"><FontAwesomeIcon icon={faEnvelope} className="ldp__info-icon" /><span>{email}</span></div>
                    )}
                    {phone && (
                      <div className="ldp__info-row"><FontAwesomeIcon icon={faPhone} className="ldp__info-icon" /><span>{phone}</span></div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {instructions && (
            <div className="ldp__section">
              <span className="ldp__section-label">{t("Delivery Instructions")}</span>
              <p className="ldp__instructions">{sanitizeForDisplay(instructions)}</p>
            </div>
          )}

          <button className={`ldp__cta ${isFormOpen ? "ldp__cta--open" : ""}`} onClick={toggleForm}>
            <span>{t("Request Material")}</span>
            <FontAwesomeIcon icon={isFormOpen ? faChevronUp : faChevronDown} className="ldp__cta-arrow" />
          </button>
        </div>
      </div>

      {/* request form modal */}
      <BaseDialog
        open={isFormOpen}
        onClose={toggleForm}
        title={t("Request Details")}
        darkMode={darkMode}
        showCloseButton
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <div className={`ldp-req__body ${darkMode ? "ldp--dark" : "ldp--light"}`}>
            {/* step 1: when */}
            <div className="ldp__step">
              <div className="ldp__step-head">
                <span className="ldp__step-num">1</span>
                <span className="ldp__step-label">{t("When")}</span>
              </div>
              <div className="ldp__step-body">
                <div className="ldp__field">
                  <label className="ldp__field-label">{t(isPickup ? "Pickup date" : "Delivery date")} <span className="ldp__req">*</span></label>
                  <DatePicker
                    value={pickupDate}
                    onChange={setPickupDate}
                    minDate={minDate}
                    maxDate={maxDate}
                    shouldDisableDate={disableDate}
                    slotProps={{ textField: { size: "small", fullWidth: true, className: "ldp__date-input" } }}
                  />
                </div>

                <div className="ldp__field">
                  <label className="ldp__field-label">{t("Time window")} <span className="ldp__req">*</span></label>
                  {(pStart || pEnd) && (
                    <span className="ldp__field-hint">
                      {t("Available")} {pStart && pEnd ? `${pStart} – ${pEnd}` : pStart ? `from ${pStart}` : `until ${pEnd}`}
                    </span>
                  )}
                  <div className="ldp__range-row">
                    <div className="ldp__field ldp__field--flex">
                      <input
                        className={`ldp__input ${errors.startTime ? "ldp__input--error" : ""}`}
                        type="time"
                        value={startTime}
                        min={pStart || undefined}
                        max={pEnd || undefined}
                        onChange={(e) => handleStartChange(e.target.value)}
                      />
                      {errors.startTime && <span className="ldp__field-error">{errors.startTime}</span>}
                    </div>
                    <span className="ldp__range-arrow">→</span>
                    <div className="ldp__field ldp__field--flex">
                      <input
                        className={`ldp__input ${errors.endTime ? "ldp__input--error" : ""}`}
                        type="time"
                        value={endTime}
                        min={pStart || undefined}
                        max={pEnd || undefined}
                        onChange={(e) => handleEndChange(e.target.value)}
                      />
                      {errors.endTime && <span className="ldp__field-error">{errors.endTime}</span>}
                    </div>
                  </div>
                </div>

                {isPickup && (
                  <div className="ldp__field">
                    <label className="ldp__field-label">{t("Vehicle")}</label>
                    <input
                      className="ldp__input"
                      type="text"
                      placeholder="e.g. SUV, van, box truck"
                      value={vehicle}
                      onChange={(e) => setVehicle(e.target.value)}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* step 2: how much */}
            <div className="ldp__step">
              <div className="ldp__step-head">
                <span className="ldp__step-num">2</span>
                <span className="ldp__step-label">{t("How much")}</span>
                {qtyNum > 0 && <span className="ldp__step-avail">{qtyNum} lbs {t("available")}</span>}
              </div>
              <div className="ldp__step-body">
                <div className="ldp__range-row">
                  <div className="ldp__field ldp__field--flex">
                    <label className="ldp__field-label">{t("Min (lbs)")} <span className="ldp__req">*</span></label>
                    <div className={`ldp__stepper ${errors.minQty ? "ldp__stepper--error" : ""}`}>
                      <button className="ldp__stepper-btn" disabled={!minQty || parseFloat(minQty) <= 0} onClick={() => setMinQty(stepVal(minQty, -1, qtyNum || undefined))}>
                        <FontAwesomeIcon icon={faMinus} />
                      </button>
                      <input
                        className="ldp__stepper-input"
                        type="number"
                        min="0"
                        max={qtyNum || undefined}
                        value={minQty}
                        onChange={(e) => setMinQty(e.target.value)}
                        placeholder="0"
                      />
                      <button className="ldp__stepper-btn" disabled={qtyNum > 0 && parseFloat(minQty) >= qtyNum} onClick={() => setMinQty(stepVal(minQty, 1, qtyNum || undefined))}>
                        <FontAwesomeIcon icon={faPlus} />
                      </button>
                    </div>
                    {errors.minQty && <span className="ldp__field-error">{errors.minQty}</span>}
                  </div>
                  <span className="ldp__range-arrow ldp__range-arrow--mid">→</span>
                  <div className="ldp__field ldp__field--flex">
                    <label className="ldp__field-label">{t("Max (lbs)")} <span className="ldp__req">*</span></label>
                    <div className={`ldp__stepper ${errors.maxQty ? "ldp__stepper--error" : ""}`}>
                      <button className="ldp__stepper-btn" disabled={!maxQty || parseFloat(maxQty) <= 0} onClick={() => setMaxQty(stepVal(maxQty, -1, qtyNum || undefined))}>
                        <FontAwesomeIcon icon={faMinus} />
                      </button>
                      <input
                        className="ldp__stepper-input"
                        type="number"
                        min="0"
                        max={qtyNum || undefined}
                        value={maxQty}
                        onChange={(e) => setMaxQty(e.target.value)}
                        placeholder="0"
                      />
                      <button className="ldp__stepper-btn" disabled={qtyNum > 0 && parseFloat(maxQty) >= qtyNum} onClick={() => setMaxQty(stepVal(maxQty, 1, qtyNum || undefined))}>
                        <FontAwesomeIcon icon={faPlus} />
                      </button>
                    </div>
                    {errors.maxQty && <span className="ldp__field-error">{errors.maxQty}</span>}
                  </div>
                </div>
              </div>
            </div>

            {/* step 3: budget */}
            <div className="ldp__step">
              <div className="ldp__step-head">
                <span className="ldp__step-num">3</span>
                <span className="ldp__step-label">{t("Budget")}</span>
              </div>
              <div className="ldp__step-body">
                {!isFree && (
                  <div className="ldp__ref-chip">
                    <span className="ldp__ref-chip-label">{t("Listing cost")}</span>
                    <span className="ldp__ref-chip-val">${Number(cost || 0).toFixed(2)}/lb</span>
                  </div>
                )}
                <div className="ldp__field">
                  <label className="ldp__field-label">{t("Your max price ($/lb)")} <span className="ldp__req">*</span></label>
                  <div className={`ldp__stepper ${errors.maxCost ? "ldp__stepper--error" : ""}`}>
                    <button className="ldp__stepper-btn" disabled={!maxCost || parseFloat(maxCost) <= 0} onClick={() => setMaxCost(stepVal(maxCost, -1, undefined))}>
                      <FontAwesomeIcon icon={faMinus} />
                    </button>
                    <input
                      className="ldp__stepper-input"
                      type="number"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      value={maxCost}
                      onChange={(e) => setMaxCost(e.target.value)}
                    />
                    <button className="ldp__stepper-btn" onClick={() => setMaxCost(stepVal(maxCost, 1, undefined))}>
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  </div>
                  {errors.maxCost && <span className="ldp__field-error">{errors.maxCost}</span>}
                </div>
              </div>
            </div>

            <div className="ldp__summary">
              <div className="ldp__summary-row">
                <span className="ldp__summary-dim">
                  {parseFloat(maxQty) || 0} lbs × ${Number(cost || 0).toFixed(2)}
                </span>
                <span className="ldp__summary-val">{totalCost}</span>
              </div>
              {parseFloat(maxCost) > 0 && (
                <div className="ldp__summary-row">
                  <span className="ldp__summary-dim">{t("Your max budget")}</span>
                  <span className="ldp__summary-val">
                    ${((parseFloat(maxQty) || 0) * parseFloat(maxCost)).toFixed(2)}
                  </span>
                </div>
              )}
              <hr className="ldp__summary-divider" />
              <div className="ldp__summary-row">
                <span className="ldp__summary-total-label">{t("Estimated total")}</span>
                <span className="ldp__summary-total-val">{totalCost}</span>
              </div>
            </div>

            <ActionButton
              variant="secondary"
              className="ldp__submit"
              onClick={handleSubmit}
              disabled={submitting || !canSubmit}
              loading={submitting}
            >
              {t("Confirm & Send")}
            </ActionButton>
          </div>
        </LocalizationProvider>
      </BaseDialog>
    </div>
  );
}

ListingDetailPanel.propTypes = {
  listing: PropTypes.object,
  companyUser: PropTypes.object,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  darkMode: PropTypes.bool,
};

export default ListingDetailPanel;
